/* What is currently being filtered out, and how to stop — STORY-040.
 *
 * Two criteria meet here and they are the same criterion twice:
 *
 *   - "the fact that filtering is on is visible without opening anything"
 *   - "each is shown individually and each is individually removable, plus a
 *     single clear all"
 *
 * Both exist because a filtered list that does not admit it is filtered looks
 * like a workspace that has lost records. That is the alarming failure this
 * screen has, and it is worse when the filters arrived in a link somebody else
 * sent — the reader never chose them and has no reason to suspect them.
 *
 * Sorting is deliberately not shown here. It changes the order of the same set
 * and hides nothing, so announcing it as an active filter would send somebody
 * hunting for records that are not missing.
 */

import type { MessageKey } from '@eventuley/i18n';
import { useTranslate } from '../../../shared/i18n/index.js';
import { Button } from '../../../shared/ui/index.js';
import type { EventsSearch } from '../filters.js';

export interface ActiveFiltersProps {
  readonly search: EventsSearch;
  readonly onChange: (next: EventsSearch) => void;
}

export function ActiveFilters({ search, onChange }: ActiveFiltersProps) {
  const t = useTranslate();

  /* An explicit list rather than a loop over the object's keys: `i18n.md`
   * forbids a constructed key, and a key per filter is what makes each chip
   * translatable. */
  const chips: { key: keyof EventsSearch; label: MessageKey }[] = [
    ...(search.status
      ? [{ key: 'status' as const, label: 'events.filters.chip_status' as const }]
      : []),
    ...(search.venue
      ? [{ key: 'venue' as const, label: 'events.filters.chip_venue' as const }]
      : []),
    ...(search.organiser
      ? [{ key: 'organiser' as const, label: 'events.filters.chip_organiser' as const }]
      : []),
    ...(search.within
      ? [{ key: 'within' as const, label: 'events.filters.chip_when' as const }]
      : []),
  ];

  if (chips.length === 0) return null;

  const without = (key: keyof EventsSearch) => {
    /* Removing a filter returns to page one, for the same reason changing one
     * does: page 4 of a wider result is a different page 4. */
    const next = { ...search, [key]: undefined };
    next.page = undefined;
    return next;
  };

  return (
    <div className="filters__active">
      <span className="filters__active-label">{t('events.filters.active')}</span>
      <ul className="filters__chips">
        {chips.map((chip) => (
          <li key={chip.key}>
            <Button
              variant="ghost"
              aria-label={t('events.filters.remove', { filter: t(chip.label) })}
              onClick={() => onChange(without(chip.key))}
            >
              {/* The × is decorative and the word carries the meaning, so a
                  reader who cannot see the symbol still knows what the button
                  removes — and the accessible name says which filter. */}
              {t(chip.label)} <span aria-hidden="true">×</span>
            </Button>
          </li>
        ))}
      </ul>
      {/* Filters only. The header above says sorting is not one of them and is
          deliberately excluded from this bar — clearing it here would
          contradict that and would throw away an ordering chosen elsewhere on
          the screen. */}
      <Button
        variant="ghost"
        onClick={() =>
          onChange({
            ...(search.sort === undefined ? {} : { sort: search.sort }),
            ...(search.dir === undefined ? {} : { dir: search.dir }),
          })
        }
      >
        {t('events.filters.clear')}
      </Button>
    </div>
  );
}
